// The structured data the marketing pages publish, and the discipline it is
// published under.
//
// WHY ANY OF IT EXISTS: entity disambiguation, and nothing else. "doc cheap" is
// two ordinary English words, so without a declaration that binds the name, the
// site, the organisation behind it and the thing it sells into one statement, a
// search engine has nothing to attach the brand to.
//
// WHAT IS DELIBERATELY ABSENT: any claim to a rich result. No FAQPage and no
// HowTo is emitted anywhere on this site, on either the marketing pages or the
// documentation. The FAQ questions on the home page are visible prose; the
// markup that once turned them into a result no longer does, and markup that
// buys nothing is still a second description of the page to keep true. A unit
// test beside this file asserts those types never appear.
//
// No per-document price is published here. The only offer a crawler is told
// about is the free monthly allowance, and its size comes from
// $lib/pricing/copy, so it is the number the page shows and the API grants.

import { OPERATOR_NAME } from "@doc-cheap/legal";
import { FREE_MONTHLY_DOCUMENTS, FREE_PLAN } from "$lib/pricing/copy";
import { OG_CARD } from "./og-card";
import { absoluteUrl, SITE_URL } from "./site";

/** The name the product is known by — the domain, which is also the brand. */
export const BRAND_NAME = "doc.cheap";

/**
 * The public profiles that are this organisation, for a crawler resolving the
 * two-word name. Only live accounts this product actually controls: a `sameAs`
 * pointing at somebody else's profile, or at a suspended one, is a false claim
 * about identity.
 */
export const SAME_AS: readonly string[] = [
  "https://gitlab.com/doccheap",
  "https://github.com/cheap-doc",
  "https://www.npmjs.com/org/doc-cheap",
  "https://pypi.org/user/doccheap/",
  "https://x.com/doccheap",
  "https://bsky.app/profile/doc.cheap",
  "https://www.youtube.com/@doccheap",
  "https://dev.to/doc_cheap",
  "https://hashnode.com/@doccheap",
  "https://medium.com/@doccheap",
];

/**
 * The square logo a crawler may show beside the name. A static file rather than
 * a route, so it is fetched without a challenge, and larger than the 112 px
 * minimum Google sets for an organisation logo.
 */
export const LOGO_PATH = "/logo.png";
export const LOGO_SIZE = 512;

/**
 * The person named as the author of the blog's articles, on each article and in
 * its structured data, and the page that says who runs the site.
 */
export const AUTHOR_NAME = "Teoh Chin Heng";
export const AUTHOR_PAGE = "/about";

/** The address on the card and in the structured data, as one string. */
export const CONTACT_EMAIL = "admin@doc.cheap";

/** The currency of the free offer's zero. */
export const OFFER_CURRENCY = "USD";

/** How the free offer describes itself, the one way the offer is stated outward. */
export const FREE_OFFER_DESCRIPTION = `${FREE_MONTHLY_DOCUMENTS} free recognitions every month`;

/** The types this site is allowed to publish. Anything else is a new decision. */
export const ALLOWED_TYPES: readonly string[] = [
  "Organization",
  "WebSite",
  // A translated page says which language it is in on a page node of its own.
  "WebPage",
  "WebAPI",
  "Offer",
  // The organisation's logo, with its size.
  "ImageObject",
  // The country pages: a page of prose about one subject, and the trail back to
  // the hub it sits under.
  "TechArticle",
  "BreadcrumbList",
  "ListItem",
  // The blog index, and each article under /blog with its named author.
  "Blog",
  "BlogPosting",
  "Person",
];

/**
 * The types that are never published here, whatever a future page wants.
 *
 * SoftwareApplication is here because Google grades it as a rich result that
 * requires a rating or a review, and this product has neither: a node without
 * one is reported as invalid and can never earn the result it asks for. The
 * product is an HTTP API, which schema.org names WebAPI.
 */
export const FORBIDDEN_TYPES: readonly string[] = ["FAQPage", "HowTo", "SoftwareApplication"];

type Node = Record<string, unknown>;

function organization(): Node {
  return {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: BRAND_NAME,
    legalName: OPERATOR_NAME,
    url: absoluteUrl("/"),
    email: CONTACT_EMAIL,
    logo: {
      "@type": "ImageObject",
      url: absoluteUrl(LOGO_PATH),
      width: LOGO_SIZE,
      height: LOGO_SIZE,
    },
    sameAs: [...SAME_AS],
  };
}

/**
 * The free monthly allowance, the only offer the structured data states. Its
 * description names it as an allowance, so the zero reads as what the month
 * grants and not as the price of every recognition. There is deliberately no
 * Product node: a merchant listing needs a price above zero, and none is
 * published.
 */
function freeOffer(): Node {
  return {
    "@type": "Offer",
    name: FREE_PLAN,
    price: "0",
    priceCurrency: OFFER_CURRENCY,
    description: FREE_OFFER_DESCRIPTION,
    availability: "https://schema.org/InStock",
    url: absoluteUrl("/pricing"),
  };
}

/**
 * The API itself: the same node on the home page and the pricing page, so both
 * describe one thing under one id.
 */
function webApi(): Node {
  return {
    // A WebAPI is a Service, so it takes a provider and an offer, and adds the
    // one property of its own: where the API is documented.
    "@type": "WebAPI",
    "@id": `${SITE_URL}/#api`,
    name: "doc.cheap passport and ID OCR API",
    description:
      "An HTTP API that reads a passport, identity card or driver licence from an image " +
      "and returns the fields as JSON.",
    url: absoluteUrl("/"),
    documentation: absoluteUrl("/docs"),
    provider: { "@id": `${SITE_URL}/#organization` },
    offers: [freeOffer()],
  };
}

/**
 * The graph the home page publishes: who we are, what the site is, and what the
 * product is — an API, with its documentation and its price.
 *
 * A graph rather than three separate blocks because they are one statement
 * about one thing, and a crawler that reads only part of it still gets a whole
 * sentence instead of a fragment.
 */
/**
 * How a translated page describes itself: its language and its own address.
 * Absent, the graph is the English page's, exactly as it always was.
 */
export interface PageLanguage {
  /** The BCP 47 tag of the page, e.g. "pt-BR". */
  readonly inLanguage: string;
  /** The page's absolute address. */
  readonly url: string;
}

/**
 * The node that says which language one version of a page is in. The site and
 * the API are one thing in every language, so their nodes are shared; the page
 * is what differs, and it is part of the site.
 */
function webPage(page: PageLanguage): Node {
  return {
    "@type": "WebPage",
    "@id": `${page.url}#webpage`,
    url: page.url,
    inLanguage: page.inLanguage,
    isPartOf: { "@id": `${SITE_URL}/#website` },
  };
}

export function homeGraph(page?: PageLanguage): Node {
  return {
    "@context": "https://schema.org",
    "@graph": [
      organization(),
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: BRAND_NAME,
        url: absoluteUrl("/"),
        inLanguage: "en",
        publisher: { "@id": `${SITE_URL}/#organization` },
      },
      ...(page === undefined ? [] : [webPage(page)]),
      webApi(),
    ],
  };
}

/**
 * The graph the pricing page publishes: the page, the API it prices with its
 * free offer, and the organisation behind both.
 *
 * `description` is the translation's one-line account of what is charged for,
 * which states no amount.
 */
export function pricingGraph(page?: PageLanguage & { description: string }): Node {
  const url = page?.url ?? absoluteUrl("/pricing");
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        inLanguage: page?.inLanguage ?? "en",
        description:
          page?.description ??
          "One passport, identity card or driver licence recognised from an image and " +
            "returned as structured JSON. Charged only when a document was actually read.",
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${SITE_URL}/#api` },
      },
      webApi(),
      organization(),
    ],
  };
}

/** One step of a breadcrumb trail: what the page is called and where it is. */
export interface Crumb {
  readonly name: string;
  readonly url: string;
}

function breadcrumbs(trail: readonly Crumb[]): Node {
  return {
    "@type": "BreadcrumbList",
    itemListElement: trail.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

/** What a country page says about itself, from its own row of the country table. */
export interface CountryArticle {
  /** The country's English name, e.g. "Germany". */
  readonly name: string;
  /** The page's absolute address. */
  readonly url: string;
  /** The page's meta description, which the article node repeats. */
  readonly description: string;
  /** A translated page's language, headline and trail; absent on the English page. */
  readonly translation?: {
    readonly inLanguage: string;
    readonly headline: string;
    /** The first step of the breadcrumb: the hub, which stays English. */
    readonly hubName: string;
    readonly keywords: readonly string[];
    /** What the article is about, in its own language. */
    readonly about: string;
  };
}

/**
 * The graph a country page publishes: the page as an article about that
 * country's passport, and its place under the passport-OCR hub.
 */
export function countryGraph(country: CountryArticle): Node {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TechArticle",
        headline: country.translation?.headline ?? `${country.name} passport OCR`,
        description: country.description,
        url: country.url,
        inLanguage: country.translation?.inLanguage ?? "en",
        about: country.translation?.about ?? `${country.name} passport recognition`,
        keywords: country.translation
          ? [...country.translation.keywords]
          : [`${country.name} passport ocr`, "mrz parser", "passport ocr api", "icao 9303"],
      },
      breadcrumbs([
        { name: country.translation?.hubName ?? "Passport OCR", url: absoluteUrl("/passport-ocr") },
        { name: country.name, url: country.url },
      ]),
    ],
  };
}

/**
 * A calendar day as the full date-time schema.org's Date-and-time fields ask
 * for. A bare "2026-09-24" is read by structured-data validators as a value
 * with no time and no zone, and is reported as invalid; the day is published
 * as its first instant in UTC, the same day the page shows. A value that
 * already carries a time is passed through unchanged.
 */
export function jsonLdDateTime(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value;
}

/** What an article under /blog says about itself. */
export interface BlogArticleFacts {
  readonly headline: string;
  readonly description: string;
  /** The article's absolute address, which is also its canonical one. */
  readonly url: string;
  /** A calendar day, YYYY-MM-DD; published as a UTC date-time. */
  readonly datePublished: string;
  /** A calendar day, YYYY-MM-DD; published as a UTC date-time. */
  readonly dateModified: string;
  readonly keywords: readonly string[];
  /** The article's language; English when absent. */
  readonly inLanguage?: string;
  /** The blog index the breadcrumb leads back to, in the article's language. */
  readonly blog?: { readonly name: string; readonly url: string };
}

/** What the blog index says about itself, in the index's own language. */
export interface BlogIndexFacts {
  readonly name: string;
  /** The index's absolute address. */
  readonly url: string;
  /** English when absent. */
  readonly inLanguage?: string;
}

/**
 * The graph the blog index publishes: the blog and the organisation that
 * publishes it. The organisation node is repeated for the reason given on
 * blogArticleGraph below.
 */
export function blogIndexGraph(blog: BlogIndexFacts): Node {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        "@id": `${blog.url}#blog`,
        name: blog.name,
        url: blog.url,
        inLanguage: blog.inLanguage ?? "en",
        publisher: { "@id": `${SITE_URL}/#organization` },
      },
      organization(),
    ],
  };
}

/**
 * The graph an article publishes: the article, the person who wrote it, the
 * organisation that published it, and its place under the blog.
 *
 * The organisation node is repeated here rather than only referenced, because a
 * reference to an id no node on the page declares resolves to nothing.
 */
export function blogArticleGraph(article: BlogArticleFacts): Node {
  const organizationRef = { "@id": `${SITE_URL}/#organization` };
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        headline: article.headline,
        description: article.description,
        url: article.url,
        mainEntityOfPage: article.url,
        isPartOf: { "@id": `${article.blog?.url ?? absoluteUrl("/blog")}#blog` },
        datePublished: jsonLdDateTime(article.datePublished),
        dateModified: jsonLdDateTime(article.dateModified),
        inLanguage: article.inLanguage ?? "en",
        keywords: [...article.keywords],
        image: absoluteUrl(OG_CARD),
        author: { "@type": "Person", name: AUTHOR_NAME, url: absoluteUrl(AUTHOR_PAGE) },
        publisher: organizationRef,
      },
      organization(),
      breadcrumbs([
        article.blog ?? { name: "Blog", url: absoluteUrl("/blog") },
        { name: article.headline, url: article.url },
      ]),
    ],
  };
}

/**
 * A graph as the text of a `<script type="application/ld+json">`.
 *
 * JSON inside a script element ends at the first `</script>` the HTML parser
 * sees, wherever it sits — inside a string included. Escaping the slash is the
 * one-character fix that keeps a legal document legal.
 */
export function jsonLdScript(graph: Node): string {
  return JSON.stringify(graph).replace(/<\//g, "<\\/");
}
